/**
 * API helpers for token-gated cleaner portal (/c/:token).
 * Calls Edge Function cleaner-portal; no Supabase auth.
 */

const getBaseUrl = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) throw new Error("Missing VITE_SUPABASE_URL");
  return `${url}/functions/v1/cleaner-portal`;
};

const getHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? "",
});

export interface PortalValidateResponse {
  property_id: string;
  property_name: string | null;
  property_address: string | null;
  tasks: PortalTask[];
}

export interface PortalTask {
  id: string;
  scheduled_date: string;
  notes: string | null;
  status: string;
  checklist_id: string | null;
  assigned_type: string;
  cleaner_id: string | null;
  claimed_at: string | null;
  override_check_out_time: string | null;
}

export async function validateToken(rawToken: string): Promise<PortalValidateResponse> {
  const res = await fetch(`${getBaseUrl()}?token=${encodeURIComponent(rawToken)}`, {
    method: "GET",
    headers: getHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? "Invalid or expired link");
  }
  return res.json();
}

export interface RegisterResponse {
  cleaner_id: string;
  property_id: string;
}

export async function registerCleaner(rawToken: string, displayName: string): Promise<RegisterResponse> {
  const res = await fetch(getBaseUrl(), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ action: "register", token: rawToken, display_name: displayName || "Cleaner" }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? "Registration failed");
  }
  return res.json();
}

export async function claimTask(rawToken: string, cleanerId: string, taskId: string): Promise<{ task_id: string; cleaner_id: string }> {
  const res = await fetch(getBaseUrl(), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ action: "claim", token: rawToken, cleaner_id: cleanerId, task_id: taskId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? "Could not claim task");
  }
  return res.json();
}

const SESSION_KEY_PREFIX = "cleaner_portal_";

export function getStoredCleanerId(rawToken: string): string | null {
  try {
    return sessionStorage.getItem(`${SESSION_KEY_PREFIX}${rawToken}`) ?? null;
  } catch {
    return null;
  }
}

export function setStoredCleanerId(rawToken: string, cleanerId: string): void {
  try {
    sessionStorage.setItem(`${SESSION_KEY_PREFIX}${rawToken}`, cleanerId);
  } catch {
    // ignore
  }
}
